import React, { useState, useEffect } from "react";

function AllottedStudents() {
  const [allottedStudents, setAllottedStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [message, setMessage] = useState("");
  const [dateInputs, setDateInputs] = useState({});
  const [departments, setDepartments] = useState([]);
  const [approvingReport, setApprovingReport] = useState({}); // Track approval loading state

  // Helper function to make authenticated requests
  const authFetch = async (url, options = {}) => {
    const token = localStorage.getItem("access_token");
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...options.headers
    };
    return fetch(url, { ...options, headers });
  };

  // Fetch allotted students and departments
  useEffect(() => {
    const fetchData = async () => {
      setLoadingStudents(true);
      setMessage("");
      
      try {
        // Fetch departments with authentication
        const deptRes = await authFetch("/api/professors/department_list");
        if (deptRes.ok) {
          const deptData = await deptRes.json();
          setDepartments(deptData);
        } else {
          const err = await deptRes.json();
          setMessage(err.detail || "Failed to fetch departments.");
        }

        // Fetch allotted students with authentication
        const res = await authFetch("/api/professors/allotted_student");
        if (res.ok) {
          const data = await res.json();
          setAllottedStudents(data);
          
          // Initialize date inputs
          const inputs = {};
          data.forEach(student => {
            inputs[student.sip_id] = {
              start_date: student.start_date || '',
              end_date: student.end_date || ''
            };
          });
          setDateInputs(inputs);
        } else {
          const err = await res.json();
          setMessage(err.detail || "Failed to fetch allotted students.");
        }
      } catch (error) {
        setMessage("Network error.");
        console.error("Fetch error:", error);
      }
      setLoadingStudents(false);
    };

    fetchData();
  }, []);

  // Handle date input change
  const handleDateChange = (sipId, field, value) => {
    setDateInputs(prev => ({
      ...prev,
      [sipId]: {
        ...prev[sipId],
        [field]: value
      }
    }));
  };

  // Set start date with authentication
  const handleSetStartDate = async (sipId) => {
    if (!dateInputs[sipId]?.start_date) {
      setMessage("Please select a start date");
      return;
    }
const student = allottedStudents.find(s => s.sip_id === sipId);

  if (student?.start_date) {
    const confirmUpdate = window.confirm(
      "A start date has already been set. Do you want to update it?"
    );

    if (!confirmUpdate) return;
  }
    try {
      const res = await authFetch(`/api/professors/set_start_date/${sipId}`, {
        method: "POST",
        body: JSON.stringify({
          date: dateInputs[sipId]?.start_date
        }),
      });

      if (res.ok) {
        setMessage(`Start date set successfully for student ${sipId}`);
        // Refresh the list
        const updatedRes = await authFetch("/api/professors/allotted_student");
        if (updatedRes.ok) {
          const updatedData = await updatedRes.json();
          setAllottedStudents(updatedData);
          
          // Update date inputs with new data
          const inputs = { ...dateInputs };
          updatedData.forEach(student => {
            if (inputs[student.sip_id]) {
              inputs[student.sip_id] = {
                start_date: student.start_date || '',
                end_date: student.end_date || ''
              };
            }
          });
          setDateInputs(inputs);
        }
      } else {
        const err = await res.json();
        setMessage(err.detail || "Failed to set start date.");
      }
    } catch (error) {
      setMessage("Network error.");
      console.error("Set start date error:", error);
    }
  };

  // Set end date with authentication
  const handleSetEndDate = async (sipId) => {
    if (!dateInputs[sipId]?.end_date) {
      setMessage("Please select an end date");
      return;
    }
const student = allottedStudents.find(s => s.sip_id === sipId);

  if (student?.end_date) {
    const confirmUpdate = window.confirm(
      "An end date has already been set. Do you want to update it?"
    );

    if (!confirmUpdate) return;
  }

    try {
      const res = await authFetch(`/api/professors/set_end_date/${sipId}`, {
        method: "POST",
        body: JSON.stringify({
          date: dateInputs[sipId]?.end_date
        }),
      });

      if (res.ok) {
        setMessage(`End date set successfully for student ${sipId}`);
        // Refresh the list
        const updatedRes = await authFetch("/api/professors/allotted_student");
        if (updatedRes.ok) {
          const updatedData = await updatedRes.json();
          setAllottedStudents(updatedData);
          
          // Update date inputs with new data
          const inputs = { ...dateInputs };
          updatedData.forEach(student => {
            if (inputs[student.sip_id]) {
              inputs[student.sip_id] = {
                start_date: student.start_date || '',
                end_date: student.end_date || ''
              };
            }
          });
          setDateInputs(inputs);
        }
      } else {
        const err = await res.json();
        setMessage(err.detail || "Failed to set end date.");
      }
    } catch (error) {
      setMessage("Network error.");
      console.error("Set end date error:", error);
    }
  };

  // Handle project report approval
  const handleApproveReport = async (sip_id, studentName, currentTitle) => {
	const finalTitle=window.prompt("Enter final project title for certificate (max 100 characters)",currentTitle || "");
	if(finalTitle==null) return;
	if(!finalTitle.trim()){
	setMessage("Project Title is required");
	return;
	}
	if(finalTitle.length>200) {
	setMessage("Project title cannot exceed 200 characters");
	return;
	}

    setApprovingReport(prev => ({ ...prev, [sip_id]: true }));
    setMessage("");
    
    try {
      const res = await authFetch(`/api/professors/project_report/${sip_id}`, {
        method: "POST",
	body: JSON.stringify({ final_project_title: finalTitle.trim()
}),
      });

      if (res.ok) {
        setMessage(`Project report approved successfully for ${studentName}`);
        // Refresh the list to update the approval status
        const updatedRes = await authFetch("/api/professors/allotted_student");
        if (updatedRes.ok) {
          setAllottedStudents(await updatedRes.json());
        }
      } else {
        const err = await res.json();
        setMessage(err.detail || "Failed to approve report.");
      }
    } catch (error) {
      setMessage("Network error while approving report.");
      console.error("Approval error:", error);
    } finally {
      setApprovingReport(prev => ({ ...prev, [sip_id]: false }));
    }
  };

  // Handle project report rejection
  const handleRejectReport = async (sip_id, studentName) => {
    if (!window.confirm(`Are you sure you want to reject the project report for ${studentName}?`)) {
      return;
    }
    
    setApprovingReport(prev => ({ ...prev, [sip_id]: true }));
    setMessage("");
    
    try {
      const res = await authFetch(`/api/professors/project_report/${sip_id}/reject`, {
        method: "POST",
      });

      if (res.ok) {
        setMessage(`Project report rejected for ${studentName}`);
        // Refresh the list to update the approval status
        const updatedRes = await authFetch("/api/professors/allotted_student");
        if (updatedRes.ok) {
          setAllottedStudents(await updatedRes.json());
        }
      } else {
        const err = await res.json();
        setMessage(err.detail || "Failed to reject report.");
      }
    } catch (error) {
      setMessage("Network error while rejecting report.");
      console.error("Rejection error:", error);
    } finally {
      setApprovingReport(prev => ({ ...prev, [sip_id]: false }));
    }
  };

  // Download project report handler
  const handleDownloadReport = async (sip_id, studentName) => {
    try {
      setMessage(""); // Clear any previous messages
      const res = await authFetch(`/api/professors/project_report/${sip_id}`);
      
      if (!res.ok) {
        const err = await res.json();
        setMessage(err.detail || "Failed to download report.");
        return;
      }
      
      // Get filename from Content-Disposition header if available
      const disposition = res.headers.get("Content-Disposition");
      let filename = "project_report.pdf";
      if (disposition && disposition.indexOf("filename=") !== -1) {
        filename = disposition
          .split("filename=")[1]
          .replace(/['"]/g, "")
          .trim();
      } else if (studentName) {
        filename = `${studentName.replace(/\s+/g, '_')}_project_report.pdf`;
      }
      
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      
      setMessage(`Report downloaded successfully for ${studentName}`);
    } catch (error) {
      setMessage("Network error while downloading report.");
      console.error("Download error:", error);
    }
  };

  // Function to get approval status badge
  const getApprovalStatusBadge = (student) => {
    // If no report has been uploaded
    if (!student.project_report_path) {
      return <span className="status-badge not-submitted">Not Submitted</span>;
    }
    
    // Report exists, check approval status
    if (student.project_report_approval === true) {
      return <span className="status-badge approved">Approved ✓</span>;
    } else if (student.project_report_approval === false) {
      return <span className="status-badge rejected">Rejected ✗</span>;
    } else {
      // Report exists but not yet reviewed (null or undefined)
      return <span className="status-badge pending">Pending Review</span>;
    }
  };

  // Check if any student needs date actions
  const anyStudentNeedsDateActions = allottedStudents.some(student => 
    !student.start_date || (student.start_date && !student.end_date)
  );

  return (
    <div className="allotted-students">
      <h2>Allotted Students</h2>
      {loadingStudents ? (
        <p>Loading...</p>
      ) : allottedStudents.length === 0 ? (
        <p>No students have been allotted to your projects yet.</p>
      ) : (
        <div className="students-table-container">
          <table className="students-table">
            <thead>
              <tr>
                <th>Student Name</th>
                <th>SIP ID</th>
                <th>Project Title</th>
                <th>Department</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Project Report Status</th>
                <th>Report Actions</th>
                <th className="actions-column">Date Actions</th>
                <th className="download-column">Report</th>
              </tr>
            </thead>
            <tbody>
              {allottedStudents.map((student) => {
                const needsStartDate = !student.start_date;
                const needsEndDate = student.start_date && !student.end_date;
                const showActions = true;

                return (
                  <tr key={student.sip_id}>
                    <td>{student.name}</td>
                    <td>{student.sip_id}</td>
                    <td>{student.project_title}</td>
                    <td>{student.department || departments.find(d => d.id === student.department_id)?.name || 'N/A'}</td>
                    
                    {/* Start Date Column */}
                    <td>
                      {student.start_date ? (
                        new Date(student.start_date).toLocaleDateString()
                      ) : (
                        <span className="no-date">Not set</span>
                      )}
                    </td>
                    
                    {/* End Date Column */}
                    <td>
                      {student.end_date ? (
                        new Date(student.end_date).toLocaleDateString()
                      ) : (
                        <span className="no-date">Not set</span>
                      )}
                    </td>
                    
                    {/* Project Report Status column */}
                    <td className="approval-status-cell">
                      {getApprovalStatusBadge(student)}
                    </td>
                    
                    {/* Report Actions column */}
                    <td className="approval-button-cell">
                      {student.project_report_path ? (
                        student.project_report_approval === true ? (
                          <span className="already-approved">✓ Approved</span>
                        ) : student.project_report_approval === false ? (
                          <button
                            onClick={() => handleApproveReport(student.sip_id, student.name,student.project_title)}
                            className="approve-report-btn"
                            disabled={approvingReport[student.sip_id]}
                          >
                            {approvingReport[student.sip_id] ? "Processing..." : "Approve"}
                          </button>
                        ) : (
                          <div className="report-actions-group">
                            <button
                              onClick={() => handleApproveReport(student.sip_id, student.name,student.project_title)}
                              className="approve-btn"
                              disabled={approvingReport[student.sip_id]}
                            >
                              ✓ Approve
                            </button>
                            <button
                              onClick={() => handleRejectReport(student.sip_id, student.name)}
                              className="reject-btn"
                              disabled={approvingReport[student.sip_id]}
                            >
                              ✗ Reject
                            </button>
                          </div>
                        )
                      ) : (
                        <span className="no-report">No report</span>
                      )}
                    </td>
                    
                    {/* Date Actions Column - Only show if any student needs it */}
                      <td className="actions-cell">
                        {showActions ? (
                          <div className="date-actions-container">
                            <div className="date-input-group">
                              <input
                                type="date"
                                className="date-input"
                                value={dateInputs[student.sip_id]?.start_date || ''}
                                onChange={(e) => handleDateChange(student.sip_id, 'start_date', e.target.value)}
                              />
                             <button
  onClick={() => handleSetStartDate(student.sip_id)}
  className="date-set-btn"
  disabled={!dateInputs[student.sip_id]?.start_date}
>
  {student.start_date ? "Update Start" : "Set Start"}
</button>
                            </div>
                            {student.start_date && (
                              <div className="date-input-group">
                                <input
                                  type="date"
                                  className="date-input"
                                  value={dateInputs[student.sip_id]?.end_date || ''}
                                  onChange={(e) => handleDateChange(student.sip_id, 'end_date', e.target.value)}
                                />
                                 <button
  onClick={() => handleSetEndDate(student.sip_id)}
  className="date-set-btn"
  disabled={!dateInputs[student.sip_id]?.end_date}
>
  {student.end_date ? "Update End" : "Set End"}
</button>
                             </div>
                            )}
                          </div>
                        ) : (
                          <span className="no-actions">-</span>
                        )}
                      </td>
                    
                    {/* Download column */}
                    <td className="download-cell">
                      {student.project_report_path ? (
                        <button
                          onClick={() => handleDownloadReport(student.sip_id, student.name)}
                          className="download-btn"
                          title="Download Project Report"
                        >
                          <span className="download-icon">📄</span> Download
                        </button>
                      ) : (
                        <span className="no-report">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {message && (
        <p className="message" style={{
          color: message.includes("success") || message.includes("downloaded") || message.includes("approved") ? "green" : "red",
          marginTop: "1rem",
          padding: "0.5rem",
          backgroundColor: message.includes("success") || message.includes("downloaded") || message.includes("approved") ? "#e8f5e9" : "#ffebee",
          borderRadius: "4px"
        }}>
          {message}
        </p>
      )}

      <style jsx>{`
        .allotted-students {
          padding: 20px;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }
        
        h2 {
          color: #bac9eb;
          margin-bottom: 1.5rem;
          font-weight: 500;
          border-left: 4px solid #cfdbee;
          padding-left: 12px;
        }
        
        .status-badge {
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 500;
          display: inline-block;
          text-align: center;
          min-width: 100px;
        }
        .status-badge.approved {
          background-color: #dcfce7;
          color: #15803d;
          border: 1px solid #86efac;
        }
        .status-badge.rejected {
          background-color: #fee2e2;
          color: #b91c1c;
          border: 1px solid #fca5a5;
        }
        .status-badge.pending {
          background-color: #fef9c3;
          color: #854d0e;
          border: 1px solid #fde047;
        }
        .status-badge.not-submitted {
          background-color: #f1f5f9;
          color: #475569;
          border: 1px solid #cbd5e1;
        }
        
        .approval-status-cell {
          text-align: center;
        }
        .approval-button-cell {
          text-align: center;
        }
        .report-actions-group {
          display: flex;
          gap: 8px;
          justify-content: center;
        }
        
        .approve-btn {
          background-color: #3b82f6;
          color: white;
          border: none;
          padding: 5px 12px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.8rem;
          font-weight: 500;
          transition: all 0.2s ease;
        }
        .approve-btn:hover:not(:disabled) {
          background-color: #2563eb;
          transform: translateY(-1px);
        }
        
        .reject-btn {
          background-color: #ef4444;
          color: white;
          border: none;
          padding: 5px 12px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.8rem;
          font-weight: 500;
          transition: all 0.2s ease;
        }
        .reject-btn:hover:not(:disabled) {
          background-color: #dc2626;
          transform: translateY(-1px);
        }
        
        .approve-btn:disabled,
        .reject-btn:disabled {
          background-color: #94a3b8;
          cursor: not-allowed;
          transform: none;
        }
        
        .approve-report-btn {
          background-color: #10b981;
          color: white;
          border: none;
          padding: 5px 12px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.8rem;
          font-weight: 500;
          transition: all 0.2s ease;
        }
        .approve-report-btn:hover:not(:disabled) {
          background-color: #059669;
          transform: translateY(-1px);
        }
        .approve-report-btn:disabled {
          background-color: #94a3b8;
          cursor: not-allowed;
        }
        
        .already-approved {
          color: #15803d;
          font-weight: 500;
          font-size: 0.85rem;
          background-color: #dcfce7;
          padding: 4px 10px;
          border-radius: 20px;
          display: inline-block;
        }
        
        .no-report {
          color: #94a3b8;
          font-size: 0.8rem;
          font-style: italic;
        }
        
        .download-btn {
          background-color: #f59e0b;
          color: white;
          border: none;
          padding: 5px 12px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.8rem;
          font-weight: 500;
          transition: all 0.2s ease;
          white-space: nowrap;
        }
        .download-btn:hover {
          background-color: #d97706;
          transform: translateY(-1px);
        }
        
        .download-icon {
          margin-right: 4px;
        }
        
        .date-input-group {
          display: flex;
          gap: 8px;
          align-items: center;
          flex-wrap: wrap;
        }
        
        .date-input {
          padding: 6px 8px;
          border: 1px solid #cbd5e1;
          border-radius: 6px;
          font-size: 0.8rem;
          background-color: white;
        }
        .date-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
        }
        
        .date-set-btn {
          background-color: #3b82f6;
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.75rem;
          font-weight: 500;
          transition: all 0.2s ease;
        }
        .date-set-btn:hover:not(:disabled) {
          background-color: #2563eb;
        }
        .date-set-btn:disabled {
          background-color: #cbd5e1;
          cursor: not-allowed;
        }
        
        .students-table-container {
          overflow-x: auto;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          background-color: white;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }
        
        .students-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.85rem;
          min-width: ${anyStudentNeedsDateActions ? '1400px' : '1200px'};
        }
        
        .students-table th,
        .students-table td {
          padding: 12px 10px;
          text-align: left;
          vertical-align: middle;
          border-bottom: 1px solid #e2e8f0;
        }
        
        .students-table th {
          background-color: #f8fafc;
          font-weight: 600;
          color: #1e293b;
          border-bottom: 2px solid #e2e8f0;
          font-size: 0.85rem;
        }
        
        .students-table tr:hover {
          background-color: #f1f5f9;
        }
        
        .students-table tr:nth-child(even) {
          background-color: #fafcff;
        }
        
        .actions-column {
          min-width: 260px;
        }
        
        .download-column {
          min-width: 100px;
          text-align: center;
        }
        
        .actions-cell {
          text-align: left;
        }
        
        .download-cell {
          text-align: center;
        }
        
        .date-actions-container {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        
        .no-date {
          color: #94a3b8;
          font-style: italic;
          font-size: 0.8rem;
        }
        
        .no-actions {
          color: #94a3b8;
          font-size: 0.8rem;
        }
        
        .message {
          margin-top: 1rem;
          padding: 0.75rem 1rem;
          border-radius: 8px;
          font-size: 0.85rem;
        }
      `}</style>
    </div>
  );
}

export default AllottedStudents;
